import { useEffect, useRef, type MouseEvent, type ReactNode } from 'react';
import { IconButton } from '../../primitives/IconButton.tsx';
import { Surface } from '../../primitives/Surface.tsx';
import { Text } from '../../primitives/Text.tsx';
import styles from './Drawer.module.css';

/**
 * Design system § 14 — Drawer / side panel.
 *
 * A right-anchored, focus-column-wide panel for a task that belongs beside its
 * list rather than replacing the whole screen — the admin content area's
 * exercise configuration is the first caller. Built from the same scrim +
 * `Surface` + focus-on-open + Escape-to-close pattern `ConfirmDelete.tsx`
 * already used for its centred confirmation; this component factors that
 * pattern out for a panel rather than duplicating it. `ConfirmDelete` keeps
 * its own centred layout — a drawer is the wrong weight for a one-sentence
 * confirmation.
 *
 * L2, NOT L3, AND THAT IS WHAT IT IS FOR. § 8 gives L3 to the one hero of a
 * screen and paints it in `grad/brand`; a drawer is a working surface someone
 * fills in fields on, which is L2's own definition — "interactive tiles,
 * popovers, dialogs". § 4 also holds `grad/brand` away from running text, and
 * a configuration form is nothing but running text and controls. The hero of
 * the screen this opens over is that screen's own content.
 *
 * THREE BANDS, AND THE MIDDLE ONE SCROLLS: a header that says what the panel
 * is and how to leave it, a body that scrolls, and a footer pinned to the
 * bottom carrying the actions. The header and footer keep a hairline against
 * the body so the panel's edges stay legible while content moves past them.
 *
 * Capped at `--focus-column-max` because a config form is exactly what § 7
 * calls a single-task column ("an exercise, a form, a sign-in").
 */

interface DrawerProps {
  open: boolean;
  title: string;
  /** One line under the title: what this panel is for. */
  description?: string;
  onClose: () => void;
  children: ReactNode;
  /** Save/Cancel or similar, pinned below the scrolling content. */
  footer?: ReactNode;
}

export function Drawer({
  open,
  title,
  description,
  onClose,
  children,
  footer,
}: DrawerProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) ref.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  function onScrimMouseDown(event: MouseEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget) onClose();
  }

  return (
    <div
      className={styles.scrim}
      data-testid="drawer-scrim"
      onMouseDown={onScrimMouseDown}
    >
      <div className={styles.panel}>
        <Surface layer={2} radius="xl">
          <div
            ref={ref}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="drawer-title"
            aria-describedby={description ? 'drawer-description' : undefined}
            className={styles.frame}
            data-testid="drawer"
          >
            <header className={styles.header}>
              <div className={styles.heading}>
                <Text as="h2" id="drawer-title" variant="subheading">
                  {title}
                </Text>
                {description ? (
                  <Text as="p" id="drawer-description" variant="body-sm" tone="muted">
                    {description}
                  </Text>
                ) : null}
              </div>
              <IconButton icon="close" label="Fechar" shape="square" onClick={onClose} />
            </header>

            <div className={styles.body}>{children}</div>

            {footer ? <footer className={styles.footer}>{footer}</footer> : null}
          </div>
        </Surface>
      </div>
    </div>
  );
}

interface DrawerSectionProps {
  title: string;
  /** One line under the section heading, where the fields need framing. */
  description?: string;
  children: ReactNode;
  testId?: string;
}

/**
 * § 14 — a form in a drawer is SECTIONS, not a column of fields.
 *
 * Three or four named groups are legible at a glance; fourteen consecutive
 * labelled inputs are a wall an administrator reads top to bottom every time
 * they come back to change one thing. The heading is `label` type rather than
 * `subheading` so it reads as a divider inside the panel and never competes
 * with the panel's own title (§ 5.2 — at most two display levels per screen).
 */
export function DrawerSection({
  title,
  description,
  children,
  testId,
}: DrawerSectionProps) {
  return (
    <section className={styles.section} data-testid={testId}>
      <div className={styles.sectionHead}>
        <Text as="h3" variant="label" tone="muted">
          {title}
        </Text>
        {description ? (
          <Text as="p" variant="body-sm" tone="muted">
            {description}
          </Text>
        ) : null}
      </div>
      <div className={styles.sectionBody}>{children}</div>
    </section>
  );
}
